import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authApi } from '@/services/api';
import {
  TruckIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface LocationState {
  passwordChangeToken: string;
  email: string;
  /** The password just entered at login, so it need not be retyped. */
  oldPassword?: string;
}

/**
 * First-login password change.
 *
 * When an administrator creates an account they set `must_change_password`,
 * and `POST /auth/login` then refuses to mint any session — it returns a
 * short-lived `passwordChangeToken` instead. The backend endpoint that
 * redeems it (`POST /auth/force-password-change`) existed from the start, but
 * nothing in the client ever called it and no screen accepted the token, so
 * the login page fell through to "استجابة الخادم غير متوقعة" and every newly
 * created user was permanently locked out.
 */
export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [oldPassword, setOldPassword] = useState(state?.oldPassword || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!state?.passwordChangeToken) navigate('/login', { replace: true });
  }, [state, navigate]);

  // Mirrors the server's rule (min 8) plus the composition advice we show.
  const rules = [
    { ok: newPassword.length >= 8, text: '8 أحرف على الأقل' },
    { ok: /[A-Za-z]/.test(newPassword), text: 'حرف واحد على الأقل' },
    { ok: /[0-9]/.test(newPassword), text: 'رقم واحد على الأقل' },
    { ok: newPassword.length > 0 && newPassword !== oldPassword, text: 'مختلفة عن كلمة المرور الحالية' },
  ];
  const matches = confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = rules.every((r) => r.ok) && matches && oldPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state?.passwordChangeToken || !canSubmit) return;
    setIsSubmitting(true);
    try {
      await authApi.forcePasswordChange(state.passwordChangeToken, oldPassword, newPassword);
      toast.success('تم تغيير كلمة المرور، سجّل الدخول بكلمتك الجديدة');
      // Deliberately back to a clean login rather than straight in: the change
      // invalidates the one-shot token and the account still has to pass OTP.
      navigate('/login', { replace: true });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'تعذّر تغيير كلمة المرور');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!state?.passwordChangeToken) return null;

  return (
    <div className="min-h-screen flex" dir="rtl">
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-primary mb-4">
              <KeyIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">تعيين كلمة مرور جديدة</h1>
            <p className="text-gray-500 mt-2 text-sm">
              هذا أول دخول لحسابك. لتفعيله، اختر كلمة مرور جديدة خاصة بك.
            </p>
            {state.email && (
              <p className="text-gray-700 text-sm mt-1 font-medium" dir="ltr">{state.email}</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Prefilled from the login attempt; editable in case the user
                arrived here with it cleared. */}
            <div>
              <label className="label">كلمة المرور الحالية</label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="input"
                autoComplete="current-password"
                dir="ltr"
              />
            </div>

            <div>
              <label className="label">كلمة المرور الجديدة</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input pl-10"
                  autoComplete="new-password"
                  dir="ltr"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showNew ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              <ul className="mt-2 space-y-1">
                {rules.map((r) => (
                  <li key={r.text} className="flex items-center gap-1.5 text-xs">
                    {r.ok
                      ? <CheckCircleIcon className="w-4 h-4 text-success-500 shrink-0" />
                      : <XCircleIcon className="w-4 h-4 text-gray-300 shrink-0" />}
                    <span className={r.ok ? 'text-success-700' : 'text-gray-500'}>{r.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <label className="label">تأكيد كلمة المرور الجديدة</label>
              <input
                type={showNew ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                autoComplete="new-password"
                dir="ltr"
              />
              {confirmPassword.length > 0 && !matches && (
                <p className="error-message">كلمتا المرور غير متطابقتين</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isSubmitting ? <div className="spinner w-5 h-5" /> : 'حفظ ومتابعة'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              العودة لتسجيل الدخول
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-8">
            © {new Date().getFullYear()} وزارة الصحة - جميع الحقوق محفوظة
          </p>
        </div>
      </div>

      <div className="hidden lg:flex lg:flex-1 gradient-primary items-center justify-center p-12">
        <div className="max-w-lg text-center text-white">
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-white/10 flex items-center justify-center">
            <TruckIcon className="w-16 h-16" />
          </div>
          <h2 className="text-3xl font-bold mb-4">نظام تتبع وإدارة سيارات الإسعاف</h2>
          <p className="text-lg text-white/80 leading-relaxed">
            لا تُشارك كلمة مرورك مع أحد. سيُطلب منك رمز تحقق بعد تسجيل الدخول.
          </p>
        </div>
      </div>
    </div>
  );
}
